import React, { FC, useEffect, useState } from 'react';
import { Box, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import {
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import WidgetService from '../../services/WidgetService';
import { useTranslation } from 'react-i18next';
import DateUtils from '../../shared/DateUtils';
import Helper from '../../shared/helpers/Helper';
import { WidgetNoData } from './WidgetNoData';

const WidgetLineChart: FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>([]);
  const [months, setMonths] = useState<any>([]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const controller = new AbortController();

    const getReviewScores = async () => {
      try {
        const response = await WidgetService.reviewScoreHistory(setLoading, controller.signal);
        if (response) {
          const chartData: any[] = [];
          const chartMonths: any[] = [];
          if (response.scores) {
            response.scores.forEach((s: any) => {
              const time = new Date(s.date).getTime();
              chartData.push({
                value: s.reviewScore,
                count: s.scoreCount,
                time: time,
              });
              chartMonths.push(time);
            });
          }
          setData(chartData);
          setMonths(chartMonths);
          setLoading(false);
        } else {
          return;
        }
      } catch (error) {
        console.error('Error fetching review score:', error);
      }
    };

    getReviewScores();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <div>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <>
          <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />
          <ResponsiveContainer
            width="95%"
            height={250}
          >
            {data.length > 0 ? (
              <ComposedChart
                data={data}
                margin={{ top: 20 }}
              >
                <CartesianGrid
                  stroke={
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.main
                      : theme.palette.primary.main
                  }
                  horizontal={false}
                  strokeDasharray={1}
                />
                <XAxis
                  tick={{ fontSize: 12 }}
                  ticks={months}
                  dataKey="time"
                  xAxisId="bottom"
                  domain={['auto', 'auto']}
                  type="number"
                  tickFormatter={(time) => DateUtils.shortMonthYear(new Date(time), i18n.language)}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  domain={[0, 100]}
                  dataKey="value"
                  name="Value"
                />
                <Line
                  yAxisId="left"
                  xAxisId="bottom"
                  type="linear"
                  dataKey="value"
                  stroke={
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main
                  }
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    fill={theme.palette.mode === 'dark' ? 'white' : undefined}
                  />
                </Line>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme.palette.background.default,
                            borderRadius: `${theme.shape.borderRadius}px`,
                            p: theme.spacing(1),
                          }}
                        >
                          <Typography variant="body2">
                            {t('branding_score_label')}: {payload[0].payload.value}
                          </Typography>
                          <Typography variant="body2">
                            {t('branding_score_count')}: {payload[0].payload.count}
                          </Typography>
                          <Typography variant="body2">
                            {t('date')}: {Helper.getFormattedDate(payload[0].payload.time, true)}
                          </Typography>
                        </Box>
                      );
                    }
                  }}
                  cursor={false}
                />
              </ComposedChart>
            ) : (
              <WidgetNoData />
            )}
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default WidgetLineChart;
